import { Component, forwardRef } from '@angular/core';
import { StartAction } from '../../../../Model/Dto/StartAction';
import { UserActionFeatureSystemsComponent } from '../userAction-featureSystem/userAction-featureSystem.component';
import { FeatureToBeRenamed } from '../../../../Model/Catalog/FeatureModels/FeatureToBeRenamedModel';
import { ValidFeatureSystem } from '../../../../Model/ValidFeatureSystem';
import { ValidFeatureService } from '../../../../Services/validFeature.service';
import { TranslateService } from '@ngx-translate/core';
import { CatalogService } from 'app/Services/CatalogManagement/catalog.service';
import { MacroService } from 'app/Services/CatalogManagement/macro.service';
import { JobService } from 'app/Services/job.service';
import { LoginService } from 'app/Services/login.service';
import { UserActionBaseComponent } from '../userAction-base.component';
import { Macro } from 'app/Model/ui/Macro';
import { OAuthService } from 'angular-oauth2-oidc';
import * as AspNetData from 'devextreme-aspnet-data-nojquery';

interface FeatureRename {
  feature: any;
  newName: string;
  newUnit: string;
}

@Component({
    selector: 'userAction-renameFeatures',
    templateUrl: 'userAction-renameFeatures.component.html',
    providers: [
        { provide: UserActionBaseComponent, useExisting: forwardRef(() => UserActionRenameFeaturesComponent) }
    ],
    standalone: false
})
export class UserActionRenameFeaturesComponent extends UserActionFeatureSystemsComponent {
  featuresDataSource: any;
  selectedFeatureSystem: ValidFeatureSystem;

  private renames = new Map<string, FeatureRename>();
  private pendingFeatureSystemName: string;

  constructor(
    public loginService: LoginService,
    public macroService: MacroService,
    public jobService: JobService,
    public translate: TranslateService,
    public catalogService: CatalogService,
    private authService: OAuthService,
    validFeatureService: ValidFeatureService
  ) {
    super(loginService, macroService, catalogService, validFeatureService);
  }

  async ngOnInit() {
    await super.ngOnInit();
    this.tryRestoreSelectedFeatureSystem();
  }

  onFeatureSystemChanged(e) {
    if (this.selectedFeatureSystem) {
      this.setupFeaturesDataSource();
    } else {
      this.featuresDataSource = null;
    }
  }

  private setupFeaturesDataSource() {
    const accessToken = this.authService.getAccessToken();
    const loadParams: any = {
      customerId: this.loginService.currentUser.customerId,
      validFeatureSystemId: this.selectedFeatureSystem.id
    };
    if (!this.macroService.workflowMode && this.catalogService.selectedCatalogId) {
      loadParams.catalogId = this.catalogService.selectedCatalogId;
    }

    this.featuresDataSource = AspNetData.createStore({
      key: 'id',
      loadUrl: 'api/ValidFeature/GetCatalogFeaturesOfSystemPaged',
      loadParams: loadParams,
      onBeforeSend(method, ajaxOptions) {
        ajaxOptions.xhrFields = { withCredentials: true };
        ajaxOptions.headers = { Authorization: `Bearer ${accessToken}` };
      }
    });
  }

  getNewName(feature: any): string {
    return this.renames.get(feature.id)?.newName ?? '';
  }

  setNewName(feature: any, value: string) {
    this.getOrCreateRename(feature).newName = value;
  }

  getNewUnit(feature: any): string {
    return this.renames.get(feature.id)?.newUnit ?? '';
  }

  setNewUnit(feature: any, value: string) {
    this.getOrCreateRename(feature).newUnit = value;
  }

  private getOrCreateRename(feature: any): FeatureRename {
    let rename = this.renames.get(feature.id);
    if (!rename) {
      rename = { feature: feature, newName: '', newUnit: '' };
      this.renames.set(feature.id, rename);
    }
    return rename;
  }

  private getFeaturesToBeRenamed(): FeatureToBeRenamed[] {
    return Array.from(this.renames.values())
      .filter(rename => rename.newName?.trim() || rename.newUnit?.trim())
      .map(rename => new FeatureToBeRenamed(rename.feature, rename.newName, rename.newUnit));
  }

  public async canStartMacro(): Promise<boolean> {
    return this.getFeaturesToBeRenamed().length > 0;
  }

  createUserAction(): StartAction {
    let userAction = new StartAction();
    userAction.catalogId = this.catalogService.catalog.id;
    userAction.customerId = this.loginService.currentUser.customerId;
    userAction.actionId = `RenameFeatures(${this.createParameterStringAsJsonString()})`;
    return userAction;
  }

  createParameterStringAsJsonString(): string {
    return JSON.stringify({
      featureSystemName: this.selectedFeatureSystem?.name,
      featuresToBeRenamed: this.getFeaturesToBeRenamed()
    });
  }

  parseParameter(parameter: string) {
    if (!parameter)
      return;
    try {
      const parsed = JSON.parse(parameter);
      this.renames.clear();
      for (const item of parsed.featuresToBeRenamed ?? []) {
        if (item?.feature?.id) {
          this.renames.set(item.feature.id, {
            feature: item.feature,
            newName: item.newName ?? '',
            newUnit: item.newUnit ?? ''
          });
        }
      }
      this.pendingFeatureSystemName = parsed.featureSystemName;
      this.tryRestoreSelectedFeatureSystem();
    } catch (error) {
      console.error('Invalid JSON string provided:', error);
    }
  }

  private tryRestoreSelectedFeatureSystem() {
    if (!this.pendingFeatureSystemName)
      return;

    const match = this.featureSystems?.find(featureSystem => featureSystem.name === this.pendingFeatureSystemName);
    if (!match)
      return;

    this.selectedFeatureSystem = match;
    this.pendingFeatureSystemName = null;
    this.setupFeaturesDataSource();
  }

  public getActionId(macro: Macro): string {
    return `RenameFeatures(${this.createParameterStringAsJsonString()})`;
  }
}
